import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProductUnit } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import './products.css';

/** Bereich „Einheiten“ der Einrichtung unter „Produkte“ (KON-111). Das Kürzel erscheint im Angebot hinter der Menge. */
export function ProductUnitsPage() {
  const { can } = useSession();
  const client = useQueryClient();
  const canWrite = can('setup.write');
  const canDelete = can('setup.delete');
  const units = useQuery({ queryKey: ['product-units', 'manage'], queryFn: async () => (await api<{ data: ProductUnit[] }>('GET', '/product-units/manage')).data });
  const refresh = () => client.invalidateQueries({ queryKey: ['product-units'] });

  const [name, setName] = useState('');
  const [symbol, setSymbol] = useState('');
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<string | null>(null);
  const [edit, setEdit] = useState({ name: '', symbol: '' });
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  const fail = (e: Error) => (e instanceof ApiError && e.status === 422 ? e.fields : { name: e instanceof ApiError ? e.message : 'Das hat nicht geklappt.' });

  const create = useMutation({
    mutationFn: () => api('POST', '/product-units', { name: name.trim(), symbol: symbol.trim() }),
    onSuccess: () => {
      setName('');
      setSymbol('');
      setCreateErrors({});
      refresh();
    },
    onError: (e) => setCreateErrors(fail(e)),
  });
  const update = useMutation({
    mutationFn: (id: string) => api('PATCH', `/product-units/${id}`, { name: edit.name.trim(), symbol: edit.symbol.trim() }),
    onSuccess: () => {
      setEditing(null);
      refresh();
    },
    onError: (e) => setEditErrors(fail(e)),
  });
  const reorder = useMutation({ mutationFn: (ids: string[]) => api('POST', '/product-units/order', { ids }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (id: string) => api('DELETE', `/product-units/${id}`), onSuccess: refresh });

  const list = units.data ?? [];
  const move = (index: number, delta: -1 | 1) => {
    const ids = list.map((u) => u.id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    reorder.mutate(ids);
  };
  const error = [reorder.error, remove.error, units.error].find((e) => e instanceof ApiError) as ApiError | undefined;
  const err = (errors: Record<string, string>, key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);

  function submit(event: FormEvent) {
    event.preventDefault();
    setCreateErrors({});
    create.mutate();
  }

  return (
    <section className="card">
      <h2>Einheiten</h2>
      <p className="muted small">
        In welchen Einheiten Sie verkaufen, zum Beispiel „Stück“, „Stunde“ oder „Quadratmeter“. Das Kürzel steht im Angebot hinter der Menge, etwa „3 Std.“. Die Reihenfolge
        gilt für die Auswahl im Produkt.
      </p>
      {error && <div className="alert alert--error" role="alert">{error.message}</div>}

      {canWrite && (
        <form className="unit-form" onSubmit={submit} noValidate>
          <label className="field">
            <span>Neue Einheit</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Zum Beispiel Palette" maxLength={40} aria-invalid={!!createErrors.name} />
            {err(createErrors, 'name')}
          </label>
          <label className="field">
            <span>Kürzel</span>
            <input className="input" value={symbol} onChange={(e) => setSymbol(e.target.value)} placeholder="Pal." maxLength={20} aria-invalid={!!createErrors.symbol} />
            {err(createErrors, 'symbol')}
          </label>
          <button className="btn btn--primary unit-form__submit" type="submit" disabled={create.isPending || !name.trim() || !symbol.trim()}>{create.isPending ? 'Wird angelegt …' : 'Einheit anlegen'}</button>
        </form>
      )}

      <table className="table table--cards">
        <thead>
          <tr>
            <th>Einheit</th>
            <th>Kürzel</th>
            <th>Produkte</th>
            <th><span className="sr-only">Aktionen</span></th>
          </tr>
        </thead>
        <tbody>
          {units.isLoading && <tr><td colSpan={4} className="muted">Wird geladen …</td></tr>}
          {!units.isLoading && list.length === 0 && <tr><td colSpan={4} className="empty">Noch keine Einheiten angelegt.</td></tr>}
          {list.map((u, index) =>
            editing === u.id ? (
              <tr key={u.id}>
                <td>
                  <input className="input" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} maxLength={40} aria-label="Bezeichnung" aria-invalid={!!editErrors.name} />
                  {err(editErrors, 'name')}
                </td>
                <td data-label="Kürzel">
                  <input className="input" value={edit.symbol} onChange={(e) => setEdit({ ...edit, symbol: e.target.value })} maxLength={20} aria-label="Kürzel" aria-invalid={!!editErrors.symbol} />
                  {err(editErrors, 'symbol')}
                </td>
                <td data-label="Produkte">{u.usageCount}</td>
                <td className="cell-actions">
                  <div className="row-actions">
                    <button type="button" className="btn btn--primary btn--sm" disabled={update.isPending} onClick={() => update.mutate(u.id)}>Speichern</button>
                    <button type="button" className="btn btn--secondary btn--sm" onClick={() => setEditing(null)}>Abbrechen</button>
                  </div>
                </td>
              </tr>
            ) : (
              <tr key={u.id}>
                <td><strong>{u.name}</strong></td>
                <td data-label="Kürzel">{u.symbol}</td>
                <td data-label="Produkte">{u.usageCount}</td>
                <td className="cell-actions">
                  <div className="row-actions">
                    {canWrite && (
                      <>
                        <button type="button" className="btn btn--secondary btn--sm" aria-label={`${u.name} nach oben`} disabled={index === 0 || reorder.isPending} onClick={() => move(index, -1)}>↑</button>
                        <button type="button" className="btn btn--secondary btn--sm" aria-label={`${u.name} nach unten`} disabled={index === list.length - 1 || reorder.isPending} onClick={() => move(index, 1)}>↓</button>
                        <button type="button" className="btn btn--secondary btn--sm" onClick={() => { setEditing(u.id); setEdit({ name: u.name, symbol: u.symbol }); setEditErrors({}); }}>Bearbeiten</button>
                      </>
                    )}
                    {canDelete && (
                      <button type="button" className="btn btn--danger btn--sm" disabled={remove.isPending} onClick={() => window.confirm(`Einheit „${u.name}“ wirklich löschen?`) && remove.mutate(u.id)}>Löschen</button>
                    )}
                  </div>
                </td>
              </tr>
            ),
          )}
        </tbody>
      </table>
      <p className="muted small">Eine Einheit, die Produkte verwenden, lässt sich nicht löschen. Angebote übernehmen das Kürzel beim Anlegen der Position und ändern sich später nicht mehr.</p>
    </section>
  );
}
